import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [validationError, setValidationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { login } = useCustomerAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setValidationError("No reset token provided. Please request a new link.");
      setValidating(false);
      return;
    }
    fetch(`${API}/api/customer/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || !data.valid) throw new Error(data.detail || "Invalid or expired link");
        setValid(true);
        setMaskedEmail(data.masked_email || "");
      })
      .catch((e) => setValidationError(e.message || "This link is invalid or expired."))
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/customer/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not reset password");
      login({ customer_id: data.customer_id, email: data.email, name: data.name }, data.session_token);
      toast.success(data.message || "Password updated successfully. You can now log in.");
      setDone(true);
      setTimeout(() => navigate("/account", { replace: true }), 1500);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex flex-col" data-testid="reset-password-page">
      <div className="px-6 py-4">
        <Link to="/login" className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-[#676664]/60 hover:text-[#9A7B4F]">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#9A7B4F] mb-3">Mahra</p>
            <h1 className="text-3xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Reset Password
            </h1>
            {maskedEmail && !done && (
              <p className="text-[12px] text-[#676664]/60 mt-2">For: {maskedEmail}</p>
            )}
          </div>

          <div className="bg-white p-8 border border-[#282828]/8">
            {validating && (
              <div className="flex items-center justify-center py-8 text-[#676664]/60" data-testid="reset-validating">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Validating reset link…
              </div>
            )}

            {!validating && !valid && (
              <div className="text-center py-6" data-testid="reset-invalid">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-[13px] text-[#282828] mb-2">This link is no longer valid</p>
                <p className="text-[12px] text-[#676664]/60 mb-6">{validationError}</p>
                <Link to="/login" className="inline-block bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-6 py-3 text-[11px] tracking-[0.15em] uppercase" data-testid="request-new-link-btn">
                  Request a New Link
                </Link>
              </div>
            )}

            {!validating && valid && done && (
              <div className="text-center py-6" data-testid="reset-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-[14px] text-[#282828]">Password updated</p>
                <p className="text-[12px] text-[#676664]/60 mt-1">Signing you in…</p>
              </div>
            )}

            {!validating && valid && !done && (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="New password (min 8 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 py-5 border-[#282828]/10 text-[13px]"
                    required minLength={8}
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#676664]/30">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="pl-10 py-5 border-[#282828]/10 text-[13px]"
                    required minLength={8}
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#282828] hover:bg-[#1a1a1a] text-white py-5 text-[11px] tracking-[0.15em] uppercase mt-2"
                  data-testid="reset-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Update Password"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
